import React, { useState, useEffect, useCallback } from 'react';
import {
  ShieldAlert, ChevronRight, Sparkles, Upload, Printer, Pencil, Trash2,
  Check, CheckCircle2, Loader, Save, Lock, Unlock, FileCheck2, Eye, Download,
} from 'lucide-react';
import { protocolesApi, employesApi } from '../../utils/api';
import PiecesJointes from '../../components/PiecesJointes';
import DocViewerModal from '../../components/DocViewerModal';
import { useApp } from '../../context/AppContext';
import { useAuth } from '../../context/AuthContext';

const CAT = {
  sante: { label: 'Santé', pill: 'bg-rose-100 text-rose-700' },
  urgence: { label: 'Urgences', pill: 'bg-red-100 text-red-700' },
  securite: { label: 'Sécurité', pill: 'bg-navy-100 text-navy-700' },
  hygiene: { label: 'Hygiène', pill: 'bg-cyan-100 text-cyan-700' },
  canicule: { label: 'Canicule', pill: 'bg-amber-100 text-amber-700' },
  pai: { label: 'PAI', pill: 'bg-gold-100 text-gold-700' },
  maltraitance: { label: 'Maltraitance', pill: 'bg-rose-100 text-rose-700' },
  sanitaire: { label: 'Sanitaire', pill: 'bg-emerald-100 text-emerald-700' },
};
const STATUT = {
  a_rediger: { label: 'À rédiger', pill: 'bg-navy-50 text-navy-400' },
  a_jour: { label: 'À jour', pill: 'bg-emerald-50 text-emerald-600' },
  a_reviser: { label: 'À réviser', pill: 'bg-amber-50 text-amber-700' },
};

function ProtocoleDetail({ proto, employes, readOnly, onChange }) {
  const [full, setFull] = useState(null);
  const [busy, setBusy] = useState(false);
  const [editing, setEditing] = useState(false);
  const [viewing, setViewing] = useState(false);
  const [form, setForm] = useState({ titre: '', contenu: '', version: '', couleur: '' });
  const fileRef = React.useRef(null);

  const load = useCallback(async () => {
    const { data } = await protocolesApi.getById(proto.id);
    setFull(data);
    setForm({ titre: data.titre || '', contenu: data.contenu || '', version: data.version || '1.0', couleur: data.couleur || '' });
  }, [proto.id]);
  useEffect(() => { load(); }, [load]);

  const act = async (fn, reloadDetail = true) => {
    setBusy(true);
    try { await fn(); if (reloadDetail) await load(); onChange(); }
    catch (err) { alert('Erreur : ' + (err.response?.data?.error || err.message)); }
    setBusy(false);
  };

  const lus = new Map((full?.lectures || []).map(l => [l.employe_id, l.date_lecture]));
  const toggleLecture = (emp) => {
    if (lus.has(emp.id)) act(() => protocolesApi.retirerLecture(proto.id, emp.id));
    else act(() => protocolesApi.marquerLu(proto.id, emp.id));
  };

  const onUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const fd = new FormData(); fd.append('fichier', file);
    await act(() => protocolesApi.upload(proto.id, fd));
  };

  if (!full) return <div className="px-5 py-6 text-navy-300 text-sm">Chargement…</div>;

  return (
    <div className="px-5 pb-5 pt-1 animate-expand">
      <div className="rounded-2xl bg-cream-100 border border-cream-300/60 p-5 space-y-5" style={full.couleur ? { borderLeft: `5px solid ${full.couleur}` } : undefined}>
        {/* Contenu */}
        {editing ? (
          <div className="space-y-2">
            <input className="form-input rounded-xl font-medium" value={form.titre} onChange={e => setForm(f => ({ ...f, titre: e.target.value }))} />
            <textarea className="form-input rounded-xl h-72 font-mono text-[12.5px] leading-relaxed" value={form.contenu}
              onChange={e => setForm(f => ({ ...f, contenu: e.target.value }))} placeholder="Contenu du protocole…" />
            <div className="flex items-center gap-2 flex-wrap">
              <input className="form-input rounded-xl w-28" value={form.version} onChange={e => setForm(f => ({ ...f, version: e.target.value }))} placeholder="Version" />
              <div className="flex items-center gap-1.5">
                <span className="text-[12px] text-navy-400">Couleur :</span>
                {['#dc2626', '#ea580c', '#d97706', '#16a34a', '#0891b2', '#2563eb', '#7c3aed', '#db2777', '#64748b'].map(c => (
                  <button key={c} type="button" onClick={() => setForm(f => ({ ...f, couleur: c }))}
                    className={`w-5 h-5 rounded-full border-2 ${form.couleur === c ? 'border-navy-800' : 'border-white'}`} style={{ backgroundColor: c }} title={c} />
                ))}
                {form.couleur && <button type="button" onClick={() => setForm(f => ({ ...f, couleur: '' }))} className="text-[11px] text-navy-300 hover:text-navy-600">effacer</button>}
              </div>
              <button disabled={busy} onClick={() => act(() => protocolesApi.update(proto.id, form)).then(() => setEditing(false))} className="apple-btn-primary"><Save size={15} /> Enregistrer</button>
              <button onClick={() => setEditing(false)} className="apple-btn-ghost">Annuler</button>
            </div>
          </div>
        ) : full.nom_fichier ? (
          <div className="space-y-2">
            <div className="flex items-center gap-1.5 text-[11px] uppercase tracking-wide font-semibold text-emerald-600">
              <Lock size={12} /> Document officiel — PDF non modifiable
            </div>
            <button type="button" onClick={() => setViewing(true)}
              className="w-full flex items-center gap-2 text-navy-700 bg-white border border-cream-300/60 rounded-xl px-4 py-3 hover:bg-cream-50">
              <Eye size={18} className="text-gold-500" /> Visualiser le protocole (PDF)
            </button>
          </div>
        ) : full.contenu ? (
          <div className="bg-white border border-cream-300/60 rounded-xl p-4 max-h-80 overflow-auto whitespace-pre-wrap text-[13px] leading-relaxed text-navy-700">
            {full.contenu}
          </div>
        ) : (
          <p className="text-[13px] text-navy-300 italic">Protocole non encore rédigé. {!readOnly && 'Générez-le avec Georges, importez un PDF, ou rédigez-le.'}</p>
        )}

        {/* Actions (édition réservée admin/direction) */}
        <div className="flex flex-wrap items-center gap-2">
          {(full.contenu || full.nom_fichier) && (
            <>
              <button type="button" onClick={() => setViewing(true)} className="apple-btn-secondary"><Eye size={15} /> Visualiser</button>
              <a href={protocolesApi.getPdfDownloadUrl(full.id)} target="_blank" rel="noopener noreferrer" className="apple-btn-ghost"><Download size={15} /> Télécharger</a>
              <a href={protocolesApi.getPdfUrl(full.id)} target="_blank" rel="noopener noreferrer" className="apple-btn-ghost"><Printer size={15} /> Imprimer</a>
            </>
          )}
          {!readOnly && !editing && (
            <>
              {full.nom_fichier ? (
                // Protocole figé : PDF officiel. On ne peut que le remplacer ou le déverrouiller.
                <>
                  <button onClick={() => fileRef.current?.click()} className="apple-btn-ghost"><Upload size={14} /> Remplacer le PDF</button>
                  <button disabled={busy} onClick={() => { if (window.confirm('Déverrouiller pour rééditer ? Le PDF actuel sera retiré et le protocole redeviendra modifiable.')) act(() => protocolesApi.deverrouiller(proto.id)); }}
                    className="apple-btn-ghost"><Unlock size={14} /> Déverrouiller</button>
                </>
              ) : (
                // Protocole en rédaction : outils d'écriture + figer en PDF
                <>
                  <button disabled={busy} onClick={() => act(() => protocolesApi.genererIa(proto.id))} className="apple-btn-gold">
                    {busy ? <Loader size={15} className="animate-spin" /> : <Sparkles size={15} />} Générer avec Georges
                  </button>
                  <button onClick={() => setEditing(true)} className="apple-btn-ghost"><Pencil size={14} /> Éditer</button>
                  <button onClick={() => fileRef.current?.click()} className="apple-btn-ghost"><Upload size={14} /> Importer un PDF</button>
                  {full.contenu && full.contenu.trim() && (
                    <button disabled={busy} onClick={() => { if (window.confirm('Figer ce protocole en PDF officiel non modifiable ? L\'équipe ne pourra plus que le consulter.')) act(() => protocolesApi.figer(proto.id)); }}
                      className="apple-btn-primary"><FileCheck2 size={15} /> Figer en PDF</button>
                  )}
                </>
              )}
              <input ref={fileRef} type="file" accept="application/pdf" className="hidden" onChange={onUpload} />
              <select value={full.statut} onChange={e => act(() => protocolesApi.updateStatut(proto.id, e.target.value))}
                className="text-[12px] border border-cream-300 rounded-lg px-2 py-1.5 bg-white text-navy-600">
                <option value="a_rediger">À rédiger</option>
                <option value="a_jour">À jour</option>
                <option value="a_reviser">À réviser</option>
              </select>
              <button disabled={busy} onClick={() => { if (window.confirm('Supprimer ce protocole ?')) act(() => protocolesApi.delete(proto.id), false); }}
                className="apple-btn-ghost ml-auto text-navy-200 hover:text-red-500 hover:bg-red-50"><Trash2 size={15} /></button>
            </>
          )}
        </div>

        {/* Photos & pièces jointes */}
        <div className="bg-white border border-cream-300/60 rounded-xl p-3">
          <PiecesJointes entiteType="protocole" entiteId={full.id} title="Photos & pièces jointes" />
        </div>

        {/* Accusés de lecture */}
        <div>
          <div className="text-[11px] uppercase tracking-wide text-gold-600 font-semibold mb-2 flex items-center gap-1.5">
            <CheckCircle2 size={12} /> Lu et compris — {lus.size}/{employes.length} salarié(s)
          </div>
          {employes.length === 0 ? (
            <p className="text-[12.5px] text-navy-300">Aucun salarié enregistré.</p>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-1.5">
              {employes.map(emp => {
                const lu = lus.has(emp.id);
                return (
                  <button key={emp.id} disabled={busy} onClick={() => toggleLecture(emp)}
                    className={`flex items-center gap-2 text-left rounded-xl border px-3 py-2 transition-colors ${lu ? 'border-emerald-200 bg-emerald-50/60' : 'border-cream-300/70 bg-white hover:bg-cream-50'}`}>
                    <span className={`w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0 ${lu ? 'bg-emerald-500 text-white' : 'border border-navy-200'}`}>
                      {lu && <Check size={12} />}
                    </span>
                    <span className="flex-1 min-w-0">
                      <span className="text-[13px] font-medium text-navy-800 block truncate">{emp.prenom} {emp.nom}</span>
                      <span className="text-[11px] text-navy-300">{lu ? `Lu le ${(lus.get(emp.id) || '').slice(0, 10)}` : emp.poste || 'à confirmer'}</span>
                    </span>
                  </button>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {viewing && (full.contenu || full.nom_fichier) && (
        <DocViewerModal
          url={protocolesApi.getPdfUrl(full.id)}
          downloadUrl={protocolesApi.getPdfDownloadUrl(full.id)}
          filename={`${full.titre || 'Protocole'}.pdf`}
          onClose={() => setViewing(false)}
        />
      )}
    </div>
  );
}

export default function ProtocolesPage() {
  const { selectedStructure } = useApp();
  const { user } = useAuth();
  // Seules Chloé et Marion peuvent modifier les protocoles et changer leur PDF
  // (droit dédié `peut_modifier_protocoles` ; Marion est admin). Reste = consultation.
  const readOnly = !(user?.role === 'admin' || user?.peut_modifier_protocoles);
  const [protos, setProtos] = useState([]);
  const [employes, setEmployes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = selectedStructure ? { structure_id: selectedStructure } : {};
      const [p, e] = await Promise.all([protocolesApi.getAll(params), employesApi.getAll(params)]);
      setProtos(p.data);
      setEmployes(e.data);
    } catch { /* noop */ }
    setLoading(false);
  }, [selectedStructure]);
  useEffect(() => { load(); }, [load]);

  const aRediger = protos.filter(p => p.statut === 'a_rediger').length;
  const grouped = protos.reduce((acc, p) => { (acc[p.categorie] = acc[p.categorie] || []).push(p); return acc; }, {});

  return (
    <div className="-m-6 p-6 min-h-full bg-cream-200">
      <div className="max-w-4xl mx-auto pb-16">
        <div className="mb-6">
          <h1 className="font-serif text-[32px] leading-tight text-navy-900">Protocoles</h1>
          <div className="w-12 h-0.5 bg-gold-400 rounded-full mt-2 mb-2.5" />
          <p className="text-navy-400 text-[14px]">
            Protocoles obligatoires EAJE/PMI {readOnly ? '— à lire et confirmer.' : '— à rédiger, importer et faire émarger.'}
            {!readOnly && aRediger > 0 && <span className="text-amber-600 font-medium"> · {aRediger} à rédiger</span>}
          </p>
        </div>

        {loading ? (
          <div className="text-center text-navy-300 py-16">Chargement…</div>
        ) : (
          <div className="space-y-6">
            {Object.entries(grouped).map(([cat, items]) => (
              <div key={cat}>
                <div className="flex items-center gap-2 mb-2 px-1">
                  <span className={`text-[11px] px-2 py-0.5 rounded-full font-medium ${CAT[cat]?.pill || 'bg-navy-100 text-navy-700'}`}>{CAT[cat]?.label || cat}</span>
                </div>
                <div className="apple-card divide-y divide-cream-300/50 overflow-hidden">
                  {items.map(p => {
                    const st = STATUT[p.statut] || STATUT.a_rediger;
                    const pct = employes.length ? Math.round((p.nb_lectures / employes.length) * 100) : 0;
                    return (
                      <div key={p.id} className={expanded === p.id ? 'bg-cream-100/50' : ''}>
                        <button onClick={() => setExpanded(expanded === p.id ? null : p.id)}
                          style={p.couleur ? { boxShadow: `inset 4px 0 0 ${p.couleur}` } : undefined}
                          className="w-full text-left px-5 py-3.5 flex items-center gap-3 hover:bg-cream-100/70 transition-colors">
                          <ShieldAlert size={18} className="flex-shrink-0" style={{ color: p.couleur || '#c0985a' }} />
                          <div className="flex-1 min-w-0">
                            <span className="font-semibold text-[14.5px] text-navy-900 block truncate">{p.titre}</span>
                            <span className="text-[12px] text-navy-300">v{p.version}{p.date_maj ? ` · MAJ ${p.date_maj}` : ''}{p.nom_fichier ? ' · 🔒 PDF officiel' : ''}</span>
                          </div>
                          {employes.length > 0 && (
                            <span className={`hidden sm:inline text-[11px] px-2 py-0.5 rounded-full font-medium ${pct === 100 ? 'bg-emerald-50 text-emerald-600' : 'bg-navy-50 text-navy-400'}`}>
                              {p.nb_lectures}/{employes.length} lu
                            </span>
                          )}
                          <span className={`text-[11px] px-2 py-0.5 rounded-full font-medium ${st.pill}`}>{st.label}</span>
                          <ChevronRight size={17} className={`text-navy-200 flex-shrink-0 transition-transform ${expanded === p.id ? 'rotate-90' : ''}`} />
                        </button>
                        {expanded === p.id && <ProtocoleDetail proto={p} employes={employes} readOnly={readOnly} onChange={load} />}
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
